import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from '../root/root-types';

export const CUSTOMER_CONTACT = 'customercontact';

/* =======================
   Types
======================= */

export interface CustomerContactDetailsProps {
  id?: string;
  mobile?: string;
  email?: string;
  name?: string;
  displayName?: string;
  profileName?: string;
  communicationPreferences?: string[];
  isPlatForm?: boolean;
  new?: boolean;
  customerId?: string;
  customerName?: string;
  companyName?: string;
  mobileNo?: string;
}

interface customerContactState {
  customerContactDetails: CustomerContactDetailsProps[];
  activeCustomerContactDetails: CustomerContactDetailsProps;
  triggerSave: {
    shouldSave: boolean;
    timestamp: number;
  };
}

const initialState: customerContactState = {
  customerContactDetails: [],
  activeCustomerContactDetails: {
    id: '',
    mobile: '',
    email: '',
    name: '',
    communicationPreferences: [],
    isPlatForm: false,
    customerId: '',
    customerName: '',
    companyName: '',
    mobileNo: '',
  },
  triggerSave: {
    shouldSave: false,
    timestamp: 0,
  },
};

export const customerContactSlice = createSlice({
  name: CUSTOMER_CONTACT,
  initialState,
  reducers: {
    addCustomersContact(
      state,
      action: PayloadAction<CustomerContactDetailsProps>
    ) {
      state.customerContactDetails = [
        ...state?.customerContactDetails,
        action.payload,
      ];
    },
    editCustomersContact(
      state,
      action: PayloadAction<CustomerContactDetailsProps>
    ) {
      state.customerContactDetails = state.customerContactDetails.map(
        (each) => {
          return each?.id === action?.payload?.id ? action?.payload : each;
        }
      );
    },
    addAllCustomerContacts(
      state,
      action: PayloadAction<CustomerContactDetailsProps[]>
    ) {
      state.customerContactDetails = action.payload;
    },
    clearActiveCustomerContact(state) {
      state.activeCustomerContactDetails = {
        id: '',
        mobile: '',
        email: '',
        name: '',
        communicationPreferences: [],
        isPlatForm: false,
        customerId: '',
      };
    },
    updateEditCustomersContact(
      state,
      action: PayloadAction<CustomerContactDetailsProps>
    ) {
      state.activeCustomerContactDetails = action.payload;
    },
    triggerCustomerSave(state) {
      state.triggerSave = {
        shouldSave: true,
        timestamp: Date.now(),
      };
    },
    // Reset trigger after save is processed
    resetSaveTrigger(state) {
      state.triggerSave = {
        shouldSave: false,
        timestamp: 0,
      };
    },
    clearCustomerContacts(state) {
      state.customerContactDetails = [];
    },
    removeCustomerContacts(state, action: PayloadAction<string>) {
      state.customerContactDetails = state.customerContactDetails.filter(
        (contact) => contact.id !== action.payload
      );

      // Optional: clear active contact if it was deleted
      if (state.activeCustomerContactDetails.id === action.payload) {
        state.activeCustomerContactDetails =
          initialState.activeCustomerContactDetails;
      }
    },
  },
});

/* =======================
   Actions
======================= */

export const {
  editCustomersContact,
  addCustomersContact,
  updateEditCustomersContact,
  clearActiveCustomerContact,
  clearCustomerContacts,
  addAllCustomerContacts,
  removeCustomerContacts,
  triggerCustomerSave,
  resetSaveTrigger,
} = customerContactSlice.actions;

/* =======================
   Selectors
======================= */

export const getCustomerContactSliceState = (
  rootState: RootState
): customerContactState => rootState[CUSTOMER_CONTACT];

export const getCustomerContactData = createSelector(
  getCustomerContactSliceState,
  (state) => state.customerContactDetails
);

export const getActiveCustomerContactDetails = createSelector(
  getCustomerContactSliceState,
  (state) => state.activeCustomerContactDetails
);
export const getSaveTrigger = createSelector(
  getCustomerContactSliceState,
  (state) => state.triggerSave
);

/* =======================
   Reducer Export
======================= */

export default customerContactSlice.reducer;
